import React, { useState } from "react";

import FormDialog from "../components/invitesDialog";
import { getMembers } from "../utils/firebase_operations";
import { PlainCard } from "./settings_components";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, doc, writeBatch } from "firebase/firestore";
import { customFirestore } from "../firebase";
import { auditMeta } from "../utils/auditMeta";
import { useSetAtom } from "jotai";
import { customAlertStateAtom } from "./custom-alert";
import {
  DataGrid,
  gridClasses,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";

const InvitesPanel = ({ companyId }: { companyId: string }) => {
  const queryClient = useQueryClient();
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const { data, isLoading } = useQuery({
    queryKey: ["INVITES", companyId],
    queryFn: async () => {
      return await getMembers(companyId);
    },
    refetchOnWindowFocus: false,
  });

  const addMembers = useMutation({
    mutationFn: async (validEmails: string[]) => {
      const batch = writeBatch(customFirestore);
      const newValidEmails = validEmails.filter(
        (validEmail) =>
          data.filter((member) => member.email === validEmail).length === 0
      );

      if (newValidEmails.length === 0) {
        setCustomAlertState({
          open: true,
          type: "warning",
          title: "No new emails to invite",
        });
      } else {
        newValidEmails.forEach((email) => {
          const docRef = doc(collection(customFirestore, "invitations"));
          batch.set(docRef, {
            email: email.toLowerCase(),
            organisationId: companyId,
            ...auditMeta(),
          });
        });
        await batch.commit();
        // await Promise.all(
        //   newValidEmails.map((email) => handleEmailSending(email))
        // );
        setCustomAlertState({
          open: true,
          type: "success",
          title: `Successfully invited emails: [${validEmails.length}]`,
        });

        await queryClient.invalidateQueries({
          queryKey: ["INVITES", companyId],
        });
      }
    },
  });

  const columns: GridColDef[] = [
    { field: "email", headerName: "Email", width: 200, flex: 1 },
    {
      field: "registered",
      headerName: "Status",
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<any, Date>) => (
        <Chip
          label={params.value ? "Signed Up" : "Pending..."}
          color={params.value ? "success" : "primary"}
        />
      ),
    },
  ];

  return (
    <PlainCard>
      <div className="grid grid-cols-2">
        <h1 className="text-3xl pl-4 pb-2 font-bold">Members </h1>
        <div className="place-self-end pr-2">{FormDialog(addMembers)}</div>
      </div>
      <div className="pt-4 pl-4 pb-8 pr-8 overflow-auto h-[calc(100%-50.25px)]">
        <DataGrid
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          disableColumnMenu={true}
          className={"bg-white"}
          rows={data}
          loading={isLoading}
          columns={columns}
          isRowSelectable={(row) => false}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: "transparent",
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: "none",
              },
          }}
        />
      </div>
    </PlainCard>
  );
};

export default InvitesPanel;
